import { CHAT_HISTORY_QUERY_KEY } from "@frontend-shared/constants";
import type {
  IChatHistoryPage,
  IPendingMessageStreamChunk,
} from "@frontend-shared/types";
import {
  createPendingStreamChunkBuffer,
  drainStreamChunks,
  enqueueStreamChunk,
  hasPendingStreamChunks,
} from "@frontend-shared/utils";
import type { InfiniteData } from "@tanstack/react-query";

import { chatHistoryQueryKey } from "@/api/chat/chat-history-query-key";
import { CHAT_STREAM_HIDDEN_FLUSH_MS } from "@/constants/websocket";

import {
  appendMessageDelta,
  appendMessageReasoning,
  setMessagePreparationProgress,
  updateChatHistoryMessage,
  updateMessageContentInPages,
} from "./chat-cache-utils";
import { queryClient } from "./query-client";
import type {
  IEnqueueChatStreamChunkProps,
  IFlushChatStreamBufferProps,
  TChatHistoryFetchKind,
} from "./types";

// NOTE: NSAI-5908 — the web half of stream coalescing (the buffer itself is
// shared, see frontend-shared/utils/chat-stream-buffer.ts). The server sends
// one `messageDelta` / `reasoningStep` frame per model token — and, while a
// PII placeholder is being held back, one full-text `messageContentUpdate`
// per visible change instead. Writing each frame into the chat cache
// re-rendered the transcript once per token, and kept doing so at full rate
// in a hidden tab, which is what earned the chat page the browser's "this tab
// is slowing your browser" intervention. Frames are parked here and drained
// on the next animation frame while the tab is visible, or about once a
// second while it is hidden, with an immediate drain the moment the tab comes
// back.
//
// Ordering is the invariant everything else rests on: a parked frame must be
// applied before any later frame of the same socket (WebSocketService drains
// this buffer ahead of every non-streaming event) and a history fetch must
// neither overtake nor drop it (see the fetch guards below and the drain
// ChatHistory runs before `fetchNextPage`).

const buffer = createPendingStreamChunkBuffer();
// NOTE: Frames written while an older page is being fetched. query-core
// snapshots the loaded pages when a `fetchNextPage` starts and splices the
// older page onto that snapshot when it lands, which drops every cache write
// made in between; those writes are journaled here and replayed once the
// page has landed (see the cache subscriber at the bottom).
const fetchMoreReplayJournal = createPendingStreamChunkBuffer();
let animationFrameId: number | null = null;
let timeoutId: ReturnType<typeof setTimeout> | null = null;

// NOTE: NSAI-5908 — E2E-only hooks (see vite-env.d.ts): Playwright installs
// the object before load so a spec can hold the scheduler in its hidden-tab
// cadence, wait until frames are actually parked and step the buffer by
// hand; in production the property is undefined and every read falls
// through.
const getE2eStreamBufferHooks = (): INsaiStreamBufferE2eHooks | undefined =>
  globalThis.window === undefined
    ? undefined
    : globalThis.window.__NSAI_E2E_STREAM_BUFFER__;

const isDocumentHidden = (): boolean => {
  const isHiddenOverride = getE2eStreamBufferHooks()?.isHiddenOverride;
  if (isHiddenOverride !== undefined) {
    return isHiddenOverride;
  }
  return (
    typeof document !== "undefined" && document.visibilityState === "hidden"
  );
};

const cancelScheduledFlush = (): void => {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
  if (timeoutId !== null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
};

const getChatHistoryFetchKind = (chatUuid: string): TChatHistoryFetchKind => {
  const query = queryClient
    .getQueryCache()
    .find({ queryKey: chatHistoryQueryKey(chatUuid), exact: true });
  if (query?.state.fetchStatus !== "fetching") {
    return "idle";
  }
  return query.state.fetchMeta?.fetchMore ? "fetchMore" : "refetch";
};

// NOTE: One copy-on-write update per message that streamed since the last
// drain; every other message keeps its reference. A full replacement lands
// first, then the deltas that followed it, then the reasoning — exactly what
// the individual frames would have done — and any landed text clears the
// preparation progress. A message that takes none of it (it is already
// closed, so the frames are dropped) keeps its reference too.
const applyChunksToChatHistory = (params: {
  oldData: InfiniteData<IChatHistoryPage>;
  chunks: Map<string, IPendingMessageStreamChunk>;
}): InfiniteData<IChatHistoryPage> => {
  let nextData = params.oldData;
  for (const [uuid, chunk] of params.chunks) {
    nextData = updateChatHistoryMessage({
      oldData: nextData,
      uuid,
      mutate: ({ pages }) => {
        let hasChanged = false;
        if (
          chunk.replacementContent !== undefined &&
          updateMessageContentInPages({
            pages,
            uuid,
            content: chunk.replacementContent,
          })
        ) {
          hasChanged = true;
        }
        if (
          chunk.content &&
          appendMessageDelta({ pages, uuid, delta: chunk.content })
        ) {
          hasChanged = true;
        }
        if (hasChanged) {
          // NOTE: Text has landed, whether appended or replaced, so the reply
          // is no longer "preparing" — a stream that runs on replacement
          // frames only (PII placeholders, egress scan) must clear it too.
          setMessagePreparationProgress({ pages, uuid, progress: undefined });
        }
        if (chunk.reasoning) {
          hasChanged =
            appendMessageReasoning({
              pages,
              uuid,
              reasoning: chunk.reasoning,
            }) || hasChanged;
        }
        return hasChanged;
      },
    });
  }
  return nextData;
};

const journalChunksForReplay = (params: {
  chatUuid: string;
  chunks: Map<string, IPendingMessageStreamChunk>;
}): void => {
  for (const [uuid, chunk] of params.chunks) {
    enqueueStreamChunk({
      buffer: fetchMoreReplayJournal,
      chatUuid: params.chatUuid,
      uuid,
      delta: chunk.content,
      reasoning: chunk.reasoning,
      replacementContent: chunk.replacementContent,
    });
  }
};

const writeChunksToCache = (params: {
  chatUuid: string;
  chunks: Map<string, IPendingMessageStreamChunk>;
}): void => {
  queryClient.setQueryData<InfiniteData<IChatHistoryPage>>(
    chatHistoryQueryKey(params.chatUuid),
    (oldData) => {
      // NOTE: No cache means nothing to append to — the frames are dropped, as
      // they were before; the history fetch that hydrates the cache carries
      // the same text.
      if (!oldData) return oldData;
      return applyChunksToChatHistory({ oldData, chunks: params.chunks });
    },
  );
  // NOTE: An older page still in flight will drop this write when it lands;
  // keep a copy to replay on top of the spliced result.
  if (getChatHistoryFetchKind(params.chatUuid) === "fetchMore") {
    journalChunksForReplay(params);
  }
};

// NOTE: Applies what is pending — for one chat, or for every chat when
// `chatUuid` is omitted. Safe to call at any time; a no-op when nothing is
// parked.
export const flushChatStreamBuffer = (
  props?: IFlushChatStreamBufferProps,
): void => {
  const drained = drainStreamChunks({ buffer, chatUuid: props?.chatUuid });
  if (!hasPendingStreamChunks({ buffer })) {
    cancelScheduledFlush();
  }
  for (const { chatUuid, chunks } of drained) {
    writeChunksToCache({ chatUuid, chunks });
  }
};

const flushAll = (): void => {
  cancelScheduledFlush();
  flushChatStreamBuffer();
};

const scheduleFlush = (): void => {
  if (animationFrameId !== null || timeoutId !== null) return;

  // NOTE: requestAnimationFrame never fires in a hidden tab — and a tab can go
  // hidden after the frame was requested — so a timer backs it in every case.
  // Whichever fires first drains everything and cancels the other.
  const hiddenFlushMs =
    getE2eStreamBufferHooks()?.hiddenFlushMsOverride ??
    CHAT_STREAM_HIDDEN_FLUSH_MS;
  timeoutId = setTimeout(flushAll, hiddenFlushMs);
  if (!isDocumentHidden() && typeof requestAnimationFrame === "function") {
    animationFrameId = requestAnimationFrame(flushAll);
  }
};

export const enqueueChatStreamChunk = ({
  chatUuid,
  uuid,
  delta,
  reasoning,
  replacementContent,
}: IEnqueueChatStreamChunkProps): void => {
  // NOTE: While a history fetch is in flight the frame is applied at once. A
  // full refetch's response is generated after the frame was emitted and
  // already carries its text, so a frame still parked when the response
  // lands would be appended a second time; applied now, the response simply
  // replaces it. An older-page fetch drops the write when it lands instead,
  // which the replay journal repairs (see writeChunksToCache).
  if (getChatHistoryFetchKind(chatUuid) !== "idle") {
    flushChatStreamBuffer({ chatUuid });
    const chunk: IPendingMessageStreamChunk = {
      content: delta ?? "",
      reasoning: reasoning ?? "",
    };
    if (replacementContent !== undefined) {
      chunk.replacementContent = replacementContent;
    }
    writeChunksToCache({ chatUuid, chunks: new Map([[uuid, chunk]]) });
    return;
  }

  enqueueStreamChunk({
    buffer,
    chatUuid,
    uuid,
    delta,
    reasoning,
    replacementContent,
  });
  scheduleFlush();
};

// NOTE: The fetch side of the ordering rules. Frames parked BEFORE a refetch
// starts must not be applied after its response, or they would land on top of
// the text the response already carries — so a fetch starting on a chat drains
// that chat right away. For an older-page fetch that drain is journaled (like
// every write made while the page is in flight) and replayed once the page
// has landed, because query-core splices the page onto the pages it
// snapshotted before dispatching this very event. ChatHistory additionally
// drains the chat before it asks for the next page, so the common case never
// needs the replay at all.
queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== "updated") return;
  const [queryKeyRoot, chatUuid] = event.query.queryKey;
  if (queryKeyRoot !== CHAT_HISTORY_QUERY_KEY || typeof chatUuid !== "string") {
    return;
  }

  if (event.action.type === "fetch") {
    if (!event.action.meta?.fetchMore) {
      // NOTE: A full refetch replaces every page, so whatever a page fetch it
      // overtook had journaled is moot.
      drainStreamChunks({ buffer: fetchMoreReplayJournal, chatUuid });
    }
    if (hasPendingStreamChunks({ buffer, chatUuid })) {
      flushChatStreamBuffer({ chatUuid });
    }
    return;
  }

  if (event.action.type === "success" && !event.action.manual) {
    // NOTE: The response landed (a manual success is one of our own cache
    // writes): put back what the older page's splice dropped.
    const journaled = drainStreamChunks({
      buffer: fetchMoreReplayJournal,
      chatUuid,
    });
    for (const entry of journaled) {
      writeChunksToCache(entry);
    }
    return;
  }

  if (event.action.type === "error") {
    // NOTE: A failed page fetch leaves the data as it is, so the writes it
    // would have dropped stand and there is nothing to replay.
    drainStreamChunks({ buffer: fetchMoreReplayJournal, chatUuid });
  }
});

const e2eStreamBufferHooks = getE2eStreamBufferHooks();
if (e2eStreamBufferHooks) {
  e2eStreamBufferHooks.hasPending = (): boolean =>
    hasPendingStreamChunks({ buffer });
  e2eStreamBufferHooks.flush = flushAll;
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (
      document.visibilityState === "visible" &&
      hasPendingStreamChunks({ buffer })
    ) {
      flushAll();
    }
  });
}
